import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { passwordStrength } from '@cfl/shared';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@/components/cargofl-ui';
import { PasswordInput, PasswordMatch, PasswordStrength } from '@/components/password-fields';
import { api, ApiError } from '@/lib/api';
import { useSignOut } from '@/lib/session';

/**
 * Change your own password (password sign-in only). Saving ends every session for this
 * person - this one included - so on success it signs out to the login page, which says
 * the password changed.
 */
export function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const signOut = useSignOut();
  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setCurrent('');
    setNext('');
    setConfirm('');
    setError('');
  }, [open]);

  const ready = current !== '' && passwordStrength(next).score > 0 && next === confirm && !saving;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!ready) return;
    setSaving(true);
    setError('');
    try {
      await api.post('/auth/me/password', { currentPassword: current, newPassword: next });
      await signOut('/login?changed=1');
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (Object.values(err.fieldErrors ?? {})[0]?.[0] ?? err.message)
          : 'Could not change the password. Check your connection and try again.',
      );
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>
              You will be signed out everywhere and asked to sign in with the new password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="pw-current">Current password</Label>
            <PasswordInput
              id="pw-current"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw-new">New password</Label>
            <PasswordInput
              id="pw-new"
              autoComplete="new-password"
              aria-describedby="pw-new-strength"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
            <PasswordStrength id="pw-new-strength" value={next} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw-confirm">Confirm new password</Label>
            <PasswordInput
              id="pw-confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            <PasswordMatch password={next} confirm={confirm} />
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ready}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : null}
              Change password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
